import usePageTitle from "../utils/usePageTitle";
import { Container, TextAreaInput, TextInput } from "common-tailwind-components";
import { Form, Formik } from "formik";
import { ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  assign_prompt_slot,
  clear_prompt_slot,
  create_prompt_snippet,
  delete_prompt_snippet,
  get_companies,
  get_prompt_slots,
  get_prompt_snippets,
} from "../api";
import type { Company, PromptSlotView, PromptSnippet } from "../api/types";
import { useLogin } from "../reducers";
import { isAdmin } from "../utils/permissions";

type Tab = "assignments" | "snippets";

type SnippetEditorValues = {
  name: string;
  content: string;
};

/** Groups the flat slot list by prompt, preserving the server's order (which
 * is the code-owned registry order). */
const groupByPrompt = (slots: PromptSlotView[]): [string, PromptSlotView[]][] => {
  const groups: [string, PromptSlotView[]][] = [];
  for (const slot of slots) {
    const last = groups[groups.length - 1];
    if (last && last[0] === slot.prompt) last[1].push(slot);
    else groups.push([slot.prompt, [slot]]);
  }
  return groups;
};

/** Groups snippets into version chains, one per root ancestor. The server's
 * newest-first order makes each chain (and the chain list itself) start with
 * the most recent version. */
const groupByLineage = (snippets: PromptSnippet[]): PromptSnippet[][] => {
  const byId = new Map(snippets.map(snippet => [snippet.id, snippet]));
  const rootOf = (snippet: PromptSnippet): string => {
    let current = snippet;
    while (current.parent_id) {
      const parent = byId.get(current.parent_id);
      if (!parent) break;
      current = parent;
    }
    return current.id;
  };
  const groups = new Map<string, PromptSnippet[]>();
  for (const snippet of snippets) {
    const root = rootOf(snippet);
    const group = groups.get(root);
    if (group) group.push(snippet);
    else groups.set(root, [snippet]);
  }
  return [...groups.values()];
};

const snippetLabel = (snippet: PromptSnippet) =>
  `${snippet.name} · ${new Date(snippet.created_at).toLocaleDateString()}`;

/** Authoring form for one immutable snippet version. Assignment is the
 * assignments tab's concern, except when launched from a slot there:
 * `applyTo` names the slot-and-layer the caller will point at the saved
 * version, shown so the side effect is explicit. */
const SnippetEditor = ({
  initial,
  parentId,
  applyTo,
  onSaved,
  onCancel,
}: {
  initial: SnippetEditorValues;
  parentId: string | null;
  applyTo: string | null;
  onSaved: (created: PromptSnippet) => Promise<void>;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (values: SnippetEditorValues) => {
    setError(null);
    try {
      const created = await create_prompt_snippet({
        name: values.name,
        content: values.content,
        parent_id: parentId,
      });
      await onSaved(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Formik<SnippetEditorValues> initialValues={initial} onSubmit={onSubmit}>
      {({ isSubmitting, values }) => (
        <Form
          onKeyDown={e => {
            if (e.key === "Escape") onCancel();
          }}
          className="space-y-2"
        >
          <TextInput
            name="name"
            wrapped={false}
            unstyled
            className="w-full rounded-md border border-outline bg-background px-2 py-1 text-sm text-text outline-none transition focus:border-primary"
            ariaLabel={t("prompt_config_snippet_name")}
            disabled={isSubmitting}
            required
          />
          <TextAreaInput
            name="content"
            wrapped={false}
            unstyled
            rows={16}
            className="min-h-64 w-full rounded-md border border-outline bg-background px-2 py-1 font-mono text-sm text-text outline-none transition focus:border-primary"
            ariaLabel={t("prompt_config_slot_text")}
            disabled={isSubmitting}
          />
          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex items-center justify-end gap-2">
            {applyTo && (
              <p className="mr-auto text-xs text-text-dim">
                {t("prompt_config_apply_target", { target: applyTo })}
              </p>
            )}
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="rounded-md border border-outline px-2.5 py-1 text-sm text-text-dim transition-colors hover:text-text"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !values.name.trim() || !values.content.trim()}
              title={applyTo ? t("prompt_config_apply_target", { target: applyTo }) : undefined}
              className="rounded-md bg-primary px-2.5 py-1 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {applyTo ? t("prompt_config_save_and_apply") : t("save")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

/** One snippet lineage: a version picker (newest preselected) with the
 * selected version's text, plus the actions on the selected version —
 * deriving a new one or deleting it together with the versions derived
 * from it. */
const SnippetChain = ({
  versions,
  onChanged,
}: {
  versions: PromptSnippet[];
  onChanged: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The selected version can disappear on reload (deleted); fall back to the
  // newest one, which is also the initial selection.
  const selected = versions.find(version => version.id === selectedId) ?? versions[0];

  const onDelete = async () => {
    setError(null);
    try {
      await delete_prompt_snippet(selected.id);
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="rounded-lg border border-outline bg-surface p-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          title={open ? t("prompt_config_hide_text") : t("prompt_config_show_text")}
          className="text-text-dim transition-colors hover:text-text"
        >
          <ChevronRight
            className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`}
            aria-hidden="true"
          />
        </button>
        <span className="text-sm text-text">{selected.name}</span>
        {versions.length > 1 ? (
          <select
            value={selected.id}
            onChange={e => setSelectedId(e.target.value)}
            aria-label={t("prompt_config_version_label")}
            className="rounded-md border border-outline bg-background px-2 py-1 text-xs text-text outline-none transition focus:border-primary"
          >
            {versions.map(version => (
              <option key={version.id} value={version.id}>
                {new Date(version.created_at).toLocaleString()}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs text-text-dim">
            {new Date(selected.created_at).toLocaleString()}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              title={t("prompt_config_new_version")}
              className="inline-flex items-center gap-1 rounded-md border border-outline px-2 py-1 text-xs text-text-dim transition-colors hover:text-text"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              {t("prompt_config_new_version")}
            </button>
          )}
          <button
            type="button"
            onClick={() => void onDelete()}
            title={t("prompt_config_delete_snippet_hint")}
            className="inline-flex items-center gap-1 rounded-md border border-outline px-2 py-1 text-xs text-text-dim transition-colors hover:text-danger"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t("delete")}
          </button>
        </div>
      </div>
      {open && (
        <pre className="mt-2 whitespace-pre-wrap rounded-md bg-background p-2 font-mono text-xs text-text">
          {selected.content}
        </pre>
      )}
      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
      {editing && (
        <div className="mt-3">
          <SnippetEditor
            initial={{ name: selected.name, content: selected.content }}
            parentId={selected.id}
            applyTo={null}
            onSaved={async created => {
              setEditing(false);
              setSelectedId(created.id);
              await onChanged();
            }}
            onCancel={() => setEditing(false)}
          />
        </div>
      )}
    </div>
  );
};

/** One slot: its override state in the selected layer and the picker that
 * repoints it (applied immediately). The expandable text is what the layer
 * effectively runs: the assigned snippet, else the deployment-wide one, else
 * the built-in. */
const AssignmentRow = ({
  slot,
  fallbackSnippet,
  snippets,
  companyLayer,
  onAssign,
  onEdit,
}: {
  slot: PromptSlotView;
  fallbackSnippet: PromptSnippet | null;
  snippets: PromptSnippet[];
  companyLayer: boolean;
  onAssign: (snippetId: string | null) => Promise<void>;
  onEdit: () => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const effectiveText = slot.snippet?.content ?? fallbackSnippet?.content ?? slot.default;

  const change = async (value: string) => {
    setBusy(true);
    try {
      await onAssign(value === "" ? null : value);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-outline bg-surface p-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          title={open ? t("prompt_config_hide_text") : t("prompt_config_show_text")}
          className="text-text-dim transition-colors hover:text-text"
        >
          <ChevronRight
            className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`}
            aria-hidden="true"
          />
        </button>
        <span className="font-mono text-sm text-text">{slot.slot}</span>
        {slot.snippet ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {t("prompt_config_customized")}
          </span>
        ) : fallbackSnippet ? (
          <span className="rounded-full bg-muted-surface px-2 py-0.5 text-xs text-text-dim">
            {t("prompt_config_deployment_text")}
          </span>
        ) : (
          <span className="rounded-full bg-muted-surface px-2 py-0.5 text-xs text-text-dim">
            {t("prompt_config_built_in")}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <select
            value={slot.snippet?.id ?? ""}
            onChange={e => void change(e.target.value)}
            disabled={busy}
            aria-label={`${slot.prompt}/${slot.slot}`}
            className="max-w-64 rounded-md border border-outline bg-background px-2 py-1 text-sm text-text outline-none transition focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">
              {companyLayer
                ? t("prompt_config_option_inherit")
                : t("prompt_config_option_built_in")}
            </option>
            {snippets.map(snippet => (
              <option key={snippet.id} value={snippet.id}>
                {snippetLabel(snippet)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={onEdit}
            title={t("edit")}
            className="inline-flex items-center gap-1 rounded-md border border-outline px-2 py-1 text-xs text-text-dim transition-colors hover:text-text"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            {t("edit")}
          </button>
        </div>
      </div>
      {open && (
        <pre className="mt-2 whitespace-pre-wrap rounded-md bg-background p-2 font-mono text-xs text-text">
          {effectiveText}
        </pre>
      )}
    </div>
  );
};

const PromptConfig = () => {
  const { t } = useTranslation();
  const { permissions } = useLogin();
  const [tab, setTab] = useState<Tab>("assignments");
  const [slots, setSlots] = useState<PromptSlotView[] | null>(null);
  const [snippets, setSnippets] = useState<PromptSnippet[] | null>(null);
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [fallbacks, setFallbacks] = useState<Map<string, PromptSnippet> | null>(null);
  const [creating, setCreating] = useState<{
    values: SnippetEditorValues;
    parentId: string | null;
    /** Slot-and-layer to point at the saved version; the layer is captured
     * at launch so changing the company dropdown afterwards cannot redirect
     * the assignment. */
    assignTo: { prompt: string; slot: string; companyId: number | null } | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  usePageTitle(t("prompt_config_title"));

  const admin = isAdmin(permissions);

  const sortedCompanies = useMemo(
    () => (companies ? [...companies].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [companies],
  );

  const showError = useCallback(
    (err: unknown) => setError(err instanceof Error ? err.message : String(err)),
    [],
  );

  const loadSlots = useCallback(async () => {
    if (companyId === null) {
      setSlots(await get_prompt_slots(null));
      setFallbacks(null);
      return;
    }
    // The deployment-wide layer is what a company slot without its own row
    // falls back to, so show it alongside the company's assignments.
    const [companySlots, deploymentSlots] = await Promise.all([
      get_prompt_slots(companyId),
      get_prompt_slots(null),
    ]);
    setSlots(companySlots);
    setFallbacks(
      new Map(
        deploymentSlots.flatMap((s): [string, PromptSnippet][] =>
          s.snippet ? [[`${s.prompt}/${s.slot}`, s.snippet]] : [],
        ),
      ),
    );
  }, [companyId]);

  const loadSnippets = useCallback(async () => {
    setSnippets(await get_prompt_snippets());
  }, []);

  useEffect(() => {
    if (!admin) return;
    let cancelled = false;
    get_companies()
      .then(list => {
        if (!cancelled) setCompanies(list.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) showError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [admin, showError]);

  useEffect(() => {
    if (!admin) return;
    let cancelled = false;
    Promise.all([loadSlots(), loadSnippets()])
      .then(() => {
        if (!cancelled) setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) showError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [loadSlots, loadSnippets, admin, showError]);

  if (!admin) {
    return (
      <Container>
        <div className="mx-auto max-w-4xl px-4 py-8">
          <Link
            to="/administration/"
            className="inline-flex text-sm text-primary transition hover:text-primary/80"
          >
            {t("back_to_executive_dashboard")}
          </Link>
          <div className="mt-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            {t("prompt_config_no_permission")}
          </div>
        </div>
      </Container>
    );
  }

  const onAssign = async (slot: PromptSlotView, snippetId: string | null) => {
    if (snippetId === null && !slot.snippet) return;
    try {
      if (snippetId === null) {
        await clear_prompt_slot(slot.prompt, slot.slot, companyId);
      } else {
        await assign_prompt_slot(slot.prompt, slot.slot, snippetId, companyId);
      }
      await loadSlots();
      setError(null);
    } catch (err) {
      showError(err);
    }
  };

  /** Open the snippet editor seeded from the text the slot currently runs
   * with, derived from the applied snippet when there is one; saving also
   * points the slot at the new version. */
  const onEditSlotText = (slot: PromptSlotView) => {
    const applied = slot.snippet ?? fallbacks?.get(`${slot.prompt}/${slot.slot}`) ?? null;
    setCreating({
      values: {
        name: applied?.name ?? `${slot.prompt}/${slot.slot}`,
        content: applied?.content ?? slot.default,
      },
      parentId: applied?.id ?? null,
      assignTo: { prompt: slot.prompt, slot: slot.slot, companyId },
    });
    setTab("snippets");
  };

  const applyTargetLabel = (assignTo: { prompt: string; slot: string; companyId: number | null }) =>
    `${assignTo.prompt}/${assignTo.slot} · ${
      assignTo.companyId === null
        ? t("prompt_config_all_companies")
        : (sortedCompanies.find(company => company.id === assignTo.companyId)?.name ??
          String(assignTo.companyId))
    }`;

  const onSnippetsChanged = async () => {
    // Deleting a snippet cascades into slot assignments, so both lists move.
    try {
      await Promise.all([loadSnippets(), loadSlots()]);
      setError(null);
    } catch (err) {
      showError(err);
    }
  };

  return (
    <Container>
      <div className="mx-auto flex max-w-4xl flex-col gap-6 px-4 pb-8 pt-7">
        <p className="text-sm text-text-dim">{t("prompt_config_description")}</p>
        <div role="tablist" className="flex gap-2">
          {(["assignments", "snippets"] as const).map(id => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                tab === id
                  ? "border-primary/40 bg-primary/10 text-primary"
                  : "border-outline bg-surface text-text-dim hover:bg-muted-surface hover:text-text"
              }`}
            >
              {id === "assignments"
                ? t("prompt_config_tab_assignments")
                : t("prompt_config_tab_snippets")}
            </button>
          ))}
        </div>
        {error && (
          <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            {error}
          </div>
        )}
        {tab === "assignments" && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <span className="font-medium text-text">{t("prompt_config_company_label")}</span>
              <select
                value={companyId === null ? "" : String(companyId)}
                onChange={e => setCompanyId(e.target.value === "" ? null : Number(e.target.value))}
                className="rounded-xl border border-outline bg-background px-3 py-2 text-text outline-none transition focus:border-primary"
              >
                <option value="">{t("prompt_config_all_companies")}</option>
                {sortedCompanies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>
            {slots &&
              groupByPrompt(slots).map(([prompt, promptSlots]) => (
                <section key={prompt} className="space-y-2">
                  <h2 className="font-mono text-base font-semibold text-text">{prompt}</h2>
                  {promptSlots.map(slot => {
                    const key = `${slot.prompt}/${slot.slot}`;
                    return (
                      <AssignmentRow
                        key={key}
                        slot={slot}
                        fallbackSnippet={fallbacks?.get(key) ?? null}
                        snippets={snippets ?? []}
                        companyLayer={companyId !== null}
                        onAssign={snippetId => onAssign(slot, snippetId)}
                        onEdit={() => onEditSlotText(slot)}
                      />
                    );
                  })}
                </section>
              ))}
          </>
        )}
        {tab === "snippets" && (
          <>
            <div>
              <button
                type="button"
                onClick={() =>
                  setCreating({
                    values: { name: "", content: "" },
                    parentId: null,
                    assignTo: null,
                  })
                }
                disabled={creating !== null}
                className="inline-flex items-center gap-1 rounded-md border border-outline px-2.5 py-1.5 text-sm text-text-dim transition-colors hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {t("prompt_config_new_snippet")}
              </button>
            </div>
            {creating && (
              <div className="rounded-lg border border-outline bg-surface p-3">
                <SnippetEditor
                  initial={creating.values}
                  parentId={creating.parentId}
                  applyTo={creating.assignTo ? applyTargetLabel(creating.assignTo) : null}
                  onSaved={async created => {
                    const assignTo = creating.assignTo;
                    setCreating(null);
                    // The version is saved either way; a failed assignment
                    // must survive the reload's error reset.
                    let assignError: unknown = null;
                    if (assignTo) {
                      try {
                        await assign_prompt_slot(
                          assignTo.prompt,
                          assignTo.slot,
                          created.id,
                          assignTo.companyId,
                        );
                      } catch (err) {
                        assignError = err;
                      }
                    }
                    await onSnippetsChanged();
                    if (assignError) showError(assignError);
                  }}
                  onCancel={() => setCreating(null)}
                />
              </div>
            )}
            {snippets &&
              (snippets.length === 0 && !creating ? (
                <p className="text-sm text-text-dim">{t("prompt_config_no_snippets")}</p>
              ) : (
                <div className="space-y-2">
                  {groupByLineage(snippets).map(chain => (
                    <SnippetChain
                      key={chain[chain.length - 1].id}
                      versions={chain}
                      onChanged={onSnippetsChanged}
                    />
                  ))}
                </div>
              ))}
          </>
        )}
      </div>
    </Container>
  );
};

export default PromptConfig;
